import { AlertCircle, CheckCircle2, FileSpreadsheet, FileText, LayoutDashboard, Loader2, XCircle } from "lucide-react";
import type { CanvasData, FileInfo, Step } from "../../api";
import Markdown from "../Markdown";
import { InlineArtifact, StatusChip, type ArtifactItem } from "./AssistantArtifacts";

/** One chat message in either engine (Agent Studio or Study Assistant). */
export interface UiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  pending?: boolean;
  error?: string;
  // Agent Studio
  fileIds?: string[];
  canvas?: CanvasData | null;
  steps?: Step[];
  model?: string | null;
  shareToken?: string | null;
  // Study Assistant
  status?: { type: string; content: string } | null;
  artifact?: ArtifactItem;
  notice?: "quota_exceeded";
  attachmentName?: string;
}

interface Props {
  message: UiMessage;
  files: Record<string, FileInfo>;
  canvasActive: boolean;
  onOpenCanvas: (messageId: string) => void;
  artifactHandlers: {
    send: (text: string) => void;
    onView: (path: string) => void;
    onDetails: (artifact: ArtifactItem) => void;
    interactive: boolean;
  };
}

export function FileChip({ file, name }: { file?: FileInfo; name?: string }) {
  const Icon = file?.kind === "document" ? FileText : FileSpreadsheet;
  const meta = file?.kind === "table" && file.summary.rows !== undefined ? `${file.summary.rows} rows` : null;
  return (
    <span className="inline-flex max-w-[220px] items-center gap-1.5 rounded-lg border border-border-default-t bg-surface px-2 py-1 text-xs text-secondary-t">
      <Icon className="h-3.5 w-3.5 shrink-0 text-adrsta-blue" />
      <span className="truncate">{file?.filename ?? name}</span>
      {meta && <span className="shrink-0 text-tertiary-t">· {meta}</span>}
    </span>
  );
}

function StepChip({ step }: { step: Step }) {
  const Icon = step.status === "running" ? Loader2 : step.status === "done" ? CheckCircle2 : XCircle;
  const color = step.status === "error" ? "var(--warning)" : step.status === "done" ? "var(--success)" : "var(--blue)";
  return (
    <span
      title={step.detail ?? undefined}
      className="inline-flex items-center gap-1.5 rounded-full bg-hover-bg px-2.5 py-1 text-xs font-semibold text-secondary-t"
    >
      <Icon className={`h-3.5 w-3.5 ${step.status === "running" ? "animate-spin" : ""}`} style={{ color }} />
      {step.label}
      {step.status === "error" && " (retrying)"}
    </span>
  );
}

export default function MessageItem({ message, files, canvasActive, onOpenCanvas, artifactHandlers }: Props) {
  if (message.role === "user") {
    return (
      <div className="flex flex-col items-end gap-2">
        {(message.fileIds?.length || message.attachmentName) && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {message.fileIds?.map((id) => <FileChip key={id} file={files[id]} name="file" />)}
            {message.attachmentName && <FileChip name={message.attachmentName} />}
          </div>
        )}
        <div
          className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md px-4 py-2.5 text-[13.5px] text-white"
          style={{ background: "linear-gradient(135deg, var(--blue), #6366f1)" }}
        >
          {message.content || <em className="opacity-80">↵ use default</em>}
        </div>
      </div>
    );
  }

  const steps = message.steps ?? [];
  const thinking = message.pending && !message.content && steps.length === 0 && !message.status;

  if (message.notice === "quota_exceeded") {
    return (
      <div className="rounded-card border px-4 py-3 text-[13px]" style={{ borderColor: "var(--warning)", background: "var(--warning-subtle)" }}>
        <div className="mb-1 font-semibold" style={{ color: "var(--warning)" }}>Chat limit reached</div>
        <div className="whitespace-pre-line text-secondary-t">{message.content}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {steps.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {steps.map((s) => (
            <StepChip key={s.id} step={s} />
          ))}
        </div>
      )}
      {message.status && <StatusChip type={message.status.type} content={message.status.content} />}

      {thinking && (
        <div className="flex items-center gap-2 text-[13px] text-tertiary-t">
          <Loader2 className="h-4 w-4 animate-spin text-adrsta-blue" /> Thinking…
        </div>
      )}

      {message.content && (
        <div>
          <Markdown>{message.content}</Markdown>
          {message.pending && <span className="studio-pulse ml-0.5 inline-block h-4 w-1.5 bg-adrsta-blue align-middle" />}
        </div>
      )}

      {message.artifact && <InlineArtifact artifact={message.artifact} {...artifactHandlers} />}

      {message.canvas && !message.pending && (
        <button
          onClick={() => onOpenCanvas(message.id)}
          className={`flex w-full max-w-sm items-center gap-3 rounded-card border px-3 py-2.5 text-left transition ${
            canvasActive ? "border-adrsta-blue" : "border-border-default-t bg-surface hover:border-adrsta-blue"
          }`}
          style={canvasActive ? { background: "var(--blue-subtle)" } : undefined}
        >
          <LayoutDashboard className="h-5 w-5 shrink-0 text-adrsta-blue" />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-primary-t">{message.canvas.title}</div>
            <div className="text-xs text-tertiary-t">{canvasActive ? "Showing in canvas" : "Open in canvas"}</div>
          </div>
        </button>
      )}

      {message.error && (
        <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[13px]" style={{ background: "var(--error-subtle)", color: "var(--error)" }}>
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {message.error}
        </div>
      )}
    </div>
  );
}
